import {Injectable} from "@angular/core";
import {initDebugger} from "felles-ng2/støttefunksjoner/debug";
import {erNil} from "felles-ng2/støttefunksjoner/nilsjekk";

const debug = initDebugger('Dvalesperre');

/**
 * Hindrer at skjermen slukkes og maskinen går i dvale, feks mens et møte tas opp, vhja
 * [Screen Wake Lock API](https://developer.mozilla.org/en-US/docs/Web/API/Screen_Wake_Lock_API).
 *
 * Nettleseren slipper sperren av seg selv når fanen skjules eller vinduet minimeres, så sperren tas
 * på nytt hver gang fanen blir synlig igjen så lenge den er {@link aktiver}t. Merk at maskinen
 * dermed kan gå i dvale mens fanen ligger i bakgrunnen – det er en begrensning i nettleserne.
 *
 * Nettlesere uten støtte for API-et ignoreres, sperren er da uten effekt.
 */
@Injectable({providedIn: 'root'})
export class Dvalesperre {
    private sperre?: WakeLockSentinel;
    private aktivert = false;

    constructor() {
        //tjenesten er en singleton som lever like lenge som appen, så lytteren fjernes aldri
        document.addEventListener('visibilitychange', () => void this.taSperreDersomAktivert());
    }

    /** Sann når nettleseren støtter sperren, slik at gui-en kan si fra om at maskinen kan gå i dvale */
    get erStøttet(): boolean {
        return !erNil(navigator.wakeLock);
    }

    /** Sperrer for dvale til {@link deaktiver} kalles. Flere kall til aktiver gir fortsatt én sperre */
    async aktiver(): Promise<void> {
        this.aktivert = true;
        await this.taSperreDersomAktivert();
    }

    /** Opphever sperren, maskinen kan igjen gå i dvale */
    async deaktiver(): Promise<void> {
        this.aktivert = false;
        const sperre = this.sperre;
        this.sperre = undefined;
        await sperre?.release();
    }

    //private hjelpemetoder

    private async taSperreDersomAktivert(): Promise<void> {
        //sperren kan bare tas når fanen er synlig, den tas da igjen ved neste visibilitychange
        if (!this.aktivert || !this.erStøttet || document.visibilityState !== 'visible') return;
        if (!erNil(this.sperre) && !this.sperre.released) return;

        try {
            const sperre = await navigator.wakeLock.request('screen');
            //deaktivert mens vi ventet på sperren
            if (!this.aktivert) {
                await sperre.release();
                return;
            }
            this.sperre = sperre;
            debug('dvalesperre tatt');
        } catch (feil) {
            //feks når batterisparing er slått på, opptaket skal ikke feile av den grunn
            debug('kunne ikke ta dvalesperre %o', feil);
        }
    }
}
